// ============================================================
// Net Exposure — Risk note (model).
//
// One instrument (a symbol, or a unifier and its members), all platforms.
// Every figure, the hedge state and the action plan are computed here, in
// code, from the exposure snapshot and the NexDay prediction. The model on
// the server only words the summary from the facts built here (the reply is
// discarded when it carries a number that is not in the facts).
//
// The plan proposes; it never trades. The dealer executes in DOM Trader.
// ============================================================

import type { PortfolioExposureSymbolRow } from '@/services/api';

// ── Inputs ───────────────────────────────────────────────────

/** A grid leg (structurally a NetExposure HedgeExposureRow). Broker view. */
export interface NoteLegIn {
  id: string;
  symbol: string;
  groupSymbol: string;
  unifierKey?: string;
  lp: string;
  lpAccount: string;
  isBBook: boolean;
  source: 'MT5' | 'CTRADER' | 'LP';
  brokerNetVol: number;       // lots, signed
  brokerNetNotional: number;  // units, signed
  avgPrice: number;           // 0 = unknown
  breakEvenPrice: number;     // 0 = unknown
  brokerFloatingPL: number;
  unpriced?: number;
  stale?: boolean;
  lpId?: string;
}

/** NexDay Symbol 360 response (the fields the note reads). */
export interface S360Lite {
  symbol: string;
  last_update: string | null;
  daily: {
    target_date: string;
    predicted_strength: number | null;
    momentum: string | null;
    predicted_trend: string | null;
    predicted_trading_range: string | null;
    predicted_high: number | null;
    predicted_low: number | null;
    predicted_midpoint: number | null;
    predicted_onefourth: number | null;
  } | null;
  trend_direction: { value: string | null } | null;
  opportunity: { opportunity: string | null; opportunity_direction: string | null; conviction: string | null } | null;
  intraday: { timeframe: string; prediction: { target_time: string; predicted_high: number | null; predicted_low: number | null; trend: number | null; momentum: string | null } | null }[];
}

export interface NoteInput {
  key: string;
  isUnifier: boolean;
  legs: NoteLegIn[];
  rows: PortfolioExposureSymbolRow[];
  exposureAsOf: string | null;
  prediction: S360Lite | null;
  /** Why there is no prediction (no mapping, service down). */
  predictionNote?: string;
  now: Date;
}

// ── Output ───────────────────────────────────────────────────

export type HedgeStatus = 'MATCHED' | 'PARTIAL' | 'OVER' | 'REVERSED' | 'ORPHAN' | 'NAKED' | 'WRONG-WAY' | 'FLAT';

export interface NoteLegOut {
  id: string; symbol: string; label: string; account: string; book: 'Clients' | 'Cover';
  side: string; units: string; lots: string; breakEven: string; price: string; pnl: string; pnlValue: number;
  stale: boolean; unpriced: boolean;
}

export interface NoteWindow {
  label: string; start: Date; end: Date; elapsed: boolean;
  high: string; low: string; trend: string; momentum: string;
}

export interface NoteForecast {
  nexdaySymbol: string;
  targetDate: string;
  stale: boolean;
  direction: 'UP' | 'DOWN' | null;
  trend: string; trendDirection: string; conviction: string; opportunity: string; momentum: string; range: string;
  dailyHigh: string; dailyLow: string; midpoint: string;
  levels: { side: 'LONG' | 'SHORT'; tp: [string, string]; entry: [string, string]; sl: [string, string] } | null;
  windows: NoteWindow[];
  /** Levels moved to Taiga's price (a different feed); null when not moved. */
  shift: { value: string; reason: string } | null;
  weak: boolean;
}

export interface NoteStep { when: string; action: string; venue: string; size: string; price: string; netAfter: string }

export interface RiskNote {
  key: string;
  generatedAt: Date;
  exposureAsOf: string | null;
  symbols: string[];
  status: HedgeStatus;
  ratio: string;
  price: string;
  clients: { side: string; units: string; lots: string; pnl: string };
  cover:   { side: string; units: string; lots: string; pnl: string };
  net:     { side: string; units: string; lots: string; pnl: string };
  sensitivity: string;
  legs: NoteLegOut[];
  where: string[];
  forecast: NoteForecast | null;
  forecastNote: string | null;
  plan: NoteStep[];
  triggers: string[];
  donts: string[];
  facts: { id: string; text: string }[];
  summary: { headline: string; points: string[] };
}

// ── Hedge state (shared with the Net Exposure grid) ──────────

/** Below this, a side counts as none (dust after rounding). */
export const LOT_EPS = 0.005;

/**
 * Status from the client book (b) and the cover (c), lots, broker view.
 * net = b + c.
 *   FLAT       no book, no cover
 *   NAKED      book, no cover
 *   ORPHAN     cover, no book
 *   WRONG-WAY  cover on the book's side: adds to the risk
 *   PARTIAL    opposed, ratio < 95%            net smaller, same side as the book
 *   MATCHED    opposed, ratio 95%-105%         net about 0
 *   OVER       opposed, ratio 105%-200%        net flipped, still smaller than the book
 *   REVERSED   opposed, ratio > 200%           net flipped and larger than the book
 * Ratio = |c| / |b|; negative for WRONG-WAY; 0 for NAKED; null without a book.
 */
export function hedgeState(bLots: number, cLots: number): { status: HedgeStatus; ratio: number | null } {
  const bAbs = Math.abs(bLots) < LOT_EPS ? 0 : Math.abs(bLots);
  const cAbs = Math.abs(cLots) < LOT_EPS ? 0 : Math.abs(cLots);
  if (bAbs === 0 && cAbs === 0) return { status: 'FLAT', ratio: null };
  if (bAbs === 0) return { status: 'ORPHAN', ratio: null };
  if (cAbs === 0) return { status: 'NAKED', ratio: 0 };
  const opposed = (bLots > 0 && cLots < 0) || (bLots < 0 && cLots > 0);
  if (!opposed) return { status: 'WRONG-WAY', ratio: -cAbs / bAbs };
  const ratio = cAbs / bAbs;
  if (ratio > 2)     return { status: 'REVERSED', ratio };
  if (ratio > 1.05)  return { status: 'OVER', ratio };
  if (ratio >= 0.95) return { status: 'MATCHED', ratio };
  return { status: 'PARTIAL', ratio };
}

// ── Formatting ───────────────────────────────────────────────

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

export function fmtUnits(u: number): string {
  const a = Math.abs(u);
  if (a >= 1_000_000) return `${(a / 1_000_000).toFixed(2)}M`;
  if (a >= 1_000) return `${(a / 1_000).toFixed(1)}K`;
  return a.toFixed(2);
}
const fmtLots = (l: number) => Math.abs(l).toFixed(2);
export function fmtUsd(v: number): string {
  const s = `$${Math.round(Math.abs(v)).toLocaleString('en-US')}`;
  return v < 0 && Math.round(Math.abs(v)) !== 0 ? `-${s}` : s;
}
const sideOf = (v: number) => (Math.abs(v) < 1e-9 ? 'Flat' : v > 0 ? 'Long' : 'Short');

/** Price digits from the price itself: 5 below 10, 3 below 1,000, else 2. */
const digitsOf = (px: number) => (px < 10 ? 5 : px < 1000 ? 3 : 2);

/** FX pairs move in pips; anything else in steps of 1.00. */
function moveOf(symbol: string, px: number): { size: number; label: string } {
  const fx = /^[A-Z]{6}/.test(symbol.toUpperCase()) && !/^(XAU|XAG|XPT|XPD|BTC|ETH)/i.test(symbol) && px < 1000;
  if (!fx) return { size: 1, label: '1.00 move' };
  return /JPY/i.test(symbol) ? { size: 0.01, label: 'pip' } : { size: 0.0001, label: 'pip' };
}

const timeOf = (d: Date) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

/** 17:00 New York today (the FX rollover), as a Date. */
function rolloverAt(now: Date): Date {
  const ny = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const offset = now.getTime() - ny.getTime();
  const at = new Date(ny);
  at.setHours(17, 0, 0, 0);
  if (at.getTime() <= ny.getTime()) at.setDate(at.getDate() + 1);
  return new Date(at.getTime() + offset);
}

const TF: Record<string, { label: string; minutes: number }> = {
  '15min': { label: '15 min', minutes: 15 },
  '30min': { label: '30 min', minutes: 30 },
  '1hour': { label: '1 hour', minutes: 60 },
  '2hour': { label: '2 hours', minutes: 120 },
};

export const RISK_NOTE_DISCLAIMER = [
  'This risk note is generated by Taiga from the book figures and the NexDay predictions at the time shown. It is decision support for the broker\'s risk manager, not an instruction: every decision, and its execution, remains the broker\'s own. Figures are a snapshot and may have changed since.',
  'NexDay predictions come from neural-network models. They can be wrong and are not necessarily accurate. They do not represent an offer to buy or sell any instrument and are not an advisory service.',
  'Taiga and NexDay accept no responsibility for any decision taken on the basis of this note or of the predictions in it.',
];

// ── Builder ──────────────────────────────────────────────────

export function buildRiskNote(inp: NoteInput): RiskNote {
  const { legs, rows, now } = inp;
  const symbols = [...new Set(legs.map((l) => l.groupSymbol))];
  const main = [...rows].sort((a, b) => Math.abs(b.net_exposure_lots) - Math.abs(a.net_exposure_lots))[0];
  const pxNum = isNum(main?.price) ? Number(main!.price) : (legs.find((l) => l.avgPrice > 0)?.avgPrice ?? 0);
  const digits = digitsOf(pxNum || 1);
  const fp = (v: number) => v.toFixed(digits);
  const move = moveOf(main?.symbol ?? inp.key, pxNum || 1);

  // Sums. Units and P/L from the legs; USD per move from the rows.
  let bLots = 0, cLots = 0, bUnits = 0, cUnits = 0, bPnl = 0, cPnl = 0;
  for (const l of legs) {
    if (l.isBBook) { bLots += l.brokerNetVol; bUnits += l.brokerNetNotional; bPnl += l.brokerFloatingPL; }
    else           { cLots += l.brokerNetVol; cUnits += l.brokerNetNotional; cPnl += l.brokerFloatingPL; }
  }
  const nLots = bLots + cLots, nUnits = bUnits + cUnits, nPnl = bPnl + cPnl;
  const rate = isNum(main?.quote_usd_rate) ? Number(main!.quote_usd_rate) : null;
  const usdPerUnitMove = rate != null ? rate : null;                  // USD per unit per 1.0 of price
  const usdPerMove = usdPerUnitMove != null ? nUnits * move.size * usdPerUnitMove : null;
  const usdOf = (units: number, priceDist: number) => (usdPerUnitMove != null ? Math.abs(units * priceDist * usdPerUnitMove) : null);

  const { status, ratio } = hedgeState(bLots, cLots);
  const ratioTxt = ratio == null ? '—' : `${(ratio * 100).toFixed(1)}%`;
  const sym = inp.key;
  const block = (lots: number, units: number, pnl: number) => ({
    side: sideOf(units), units: fmtUnits(units), lots: fmtLots(lots), pnl: fmtUsd(pnl),
  });
  const clients = block(bLots, bUnits, bPnl);
  const cover = block(cLots, cUnits, cPnl);
  const net = block(nLots, nUnits, nPnl);
  const sensitivity = usdPerMove == null ? '—' : `${fmtUsd(usdPerMove)} per ${move.label} up`;

  const legsOut: NoteLegOut[] = [...legs]
    .sort((a, b) => Number(b.isBBook) - Number(a.isBBook) || Math.abs(b.brokerNetNotional) - Math.abs(a.brokerNetNotional))
    .map((l) => ({
      id: l.id, symbol: l.symbol, label: l.lp, account: l.lpAccount, book: l.isBBook ? 'Clients' : 'Cover',
      side: sideOf(l.brokerNetNotional), units: fmtUnits(l.brokerNetNotional), lots: fmtLots(l.brokerNetVol),
      breakEven: l.breakEvenPrice > 0 ? fp(l.breakEvenPrice) : '—', price: l.avgPrice > 0 ? fp(l.avgPrice) : '—',
      pnl: fmtUsd(l.brokerFloatingPL), pnlValue: l.brokerFloatingPL, stale: !!l.stale, unpriced: !!l.unpriced,
    }));

  // ── Where the risk comes from ──────────────────────────────
  const bSide = clients.side.toLowerCase(), nSide = net.side.toLowerCase();
  const where: string[] = [];
  switch (status) {
    case 'WRONG-WAY':
      where.push(`The cover is on the same side as the clients: the broker is ${bSide} ${clients.units} from clients and the cover adds ${cover.units} on top instead of offsetting it.`);
      break;
    case 'REVERSED':
      where.push(`The cover (${cover.units}) is ${ratioTxt} of the client book (${clients.units}): the net is ${nSide} and larger than with no cover at all.`);
      break;
    case 'OVER':
      where.push(`The cover (${cover.units}) is ${ratioTxt} of the client book (${clients.units}): the net has flipped to ${nSide} ${net.units}, still smaller than the client book.`);
      break;
    case 'PARTIAL':
      where.push(`The cover offsets ${ratioTxt} of the client book: ${net.units} stays open ${nSide}.`);
      break;
    case 'NAKED':
      where.push(`The client book (${clients.side.toLowerCase()} ${clients.units}) has no cover.`);
      break;
    case 'ORPHAN':
      where.push(`The cover (${cover.side.toLowerCase()} ${cover.units}) has no client book left to offset.`);
      break;
    case 'MATCHED':
      where.push(`The cover offsets the client book (${ratioTxt}). Net ${net.units}.`);
      break;
    case 'FLAT':
      where.push('No open position in this instrument.');
      break;
  }
  // Cover legs on both sides at different LPs: gross positions that net out.
  const coverLegs = legs.filter((l) => !l.isBBook && Math.abs(l.brokerNetVol) >= LOT_EPS);
  const coverLong = coverLegs.filter((l) => l.brokerNetVol > 0), coverShort = coverLegs.filter((l) => l.brokerNetVol < 0);
  const offsetting = coverLong.length > 0 && coverShort.length > 0;
  if (offsetting) {
    where.push(`Cover is held on both sides at different venues (${coverLong.map((l) => l.lp).join(', ')} long; ${coverShort.map((l) => l.lp).join(', ')} short): gross positions that partly cancel.`);
  }
  // Client legs in profit against the broker.
  for (const l of legs.filter((x) => x.isBBook && x.brokerFloatingPL < 0 && x.breakEvenPrice > 0 && pxNum > 0)) {
    const pips = Math.abs(pxNum - l.breakEvenPrice) / move.size;
    where.push(`${l.lp}: clients are ${sideOf(-l.brokerNetNotional).toLowerCase()} from ${fp(l.breakEvenPrice)} and ${pips.toFixed(0)} ${move.label === 'pip' ? 'pips' : 'points'} in profit (${fmtUsd(l.brokerFloatingPL)} to the broker).`);
  }

  // ── Forecast ───────────────────────────────────────────────
  let forecast: NoteForecast | null = null;
  let shiftVal = 0;
  const p = inp.prediction;
  if (p) {
    const d = p.daily;
    const op = p.opportunity;
    const od = (op?.opportunity_direction ?? '').toUpperCase();
    const tr = (d?.predicted_trend ?? '').toLowerCase();
    const tdv = (p.trend_direction?.value ?? '').toUpperCase();
    const direction: 'UP' | 'DOWN' | null =
      od === 'UP' ? 'UP' : od === 'DOWN' ? 'DOWN'
      : tr === 'up' ? 'UP' : tr === 'down' ? 'DOWN'
      : tdv.includes('BULL') ? 'UP' : tdv.includes('BEAR') ? 'DOWN' : null;

    // Windows, newest prediction per timeframe.
    const wins = (p.intraday ?? [])
      .filter((w) => w.prediction && TF[w.timeframe] && isNum(w.prediction.predicted_high) && isNum(w.prediction.predicted_low))
      .map((w) => {
        const end = new Date(w.prediction!.target_time);
        const tf = TF[w.timeframe];
        return {
          tf: w.timeframe, label: tf.label, end, start: new Date(end.getTime() - tf.minutes * 60_000),
          hi: Number(w.prediction!.predicted_high), lo: Number(w.prediction!.predicted_low),
          trend: w.prediction!.trend, momentum: w.prediction!.momentum ?? '—',
        };
      })
      .filter((w) => !isNaN(w.end.getTime()));

    // Price basis: NexDay prices its own feed. Anchor = middle of the
    // shortest window still running; when Taiga's price is more than a
    // quarter of the daily range away from it, the levels are moved by the gap.
    const dHi = isNum(d?.predicted_high) ? Number(d!.predicted_high) : null;
    const dLo = isNum(d?.predicted_low) ? Number(d!.predicted_low) : null;
    const live = wins.filter((w) => w.end.getTime() > now.getTime()).sort((a, b) => a.end.getTime() - b.end.getTime());
    const anchorWin = live[0] ?? null;
    let shift: NoteForecast['shift'] = null;
    if (anchorWin && pxNum > 0) {
      const anchor = (anchorWin.hi + anchorWin.lo) / 2;
      const span = dHi != null && dLo != null ? dHi - dLo : anchorWin.hi - anchorWin.lo;
      const gap = pxNum - anchor;
      if (span > 0 && Math.abs(gap) > span / 4) {
        shiftVal = gap;
        shift = {
          value: `${gap >= 0 ? '+' : '-'}${Math.abs(gap).toFixed(digits)}`,
          reason: `NexDay prices its own feed: the middle of its ${anchorWin.label} window (${fp(anchor)}) is ${Math.abs(gap / move.size).toFixed(0)} ${move.label === 'pip' ? 'pips' : 'points'} from Taiga's price (${fp(pxNum)}). NexDay levels below are moved by that gap.`,
        };
      }
    }
    const sp = (v: number | null) => (v == null ? '—' : fp(v + shiftVal));

    // GoPredict trading levels (same rule as the Predictions page).
    let levels: NoteForecast['levels'] = null;
    if (dHi != null && dLo != null && direction) {
      const mid = isNum(d?.predicted_midpoint) ? Number(d!.predicted_midpoint) : (dHi + dLo) / 2;
      const q = isNum(d?.predicted_onefourth) ? Number(d!.predicted_onefourth) : (dHi - dLo) / 4;
      const r = (v: number) => fp(v + shiftVal);
      levels = direction === 'DOWN'
        ? { side: 'SHORT', tp: [r(dLo), r(dLo + q)], entry: [r(mid), r(mid + q)], sl: [r(dHi + q), r(dHi)] }
        : { side: 'LONG',  tp: [r(dHi), r(dHi - q)], entry: [r(mid), r(mid - q)], sl: [r(dLo - q), r(dLo)] };
    }

    const todayEt = now.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    const conviction = op?.conviction ?? '—';
    const opportunity = op?.opportunity ?? '—';
    forecast = {
      nexdaySymbol: p.symbol,
      targetDate: d?.target_date?.slice(0, 10) ?? '—',
      stale: !!d?.target_date && d.target_date.slice(0, 10) < todayEt,
      direction,
      trend: d?.predicted_trend ?? '—',
      trendDirection: p.trend_direction?.value ?? '—',
      conviction, opportunity,
      momentum: d?.momentum ?? '—',
      range: d?.predicted_trading_range ?? '—',
      dailyHigh: sp(dHi), dailyLow: sp(dLo),
      midpoint: sp(isNum(d?.predicted_midpoint) ? Number(d!.predicted_midpoint) : dHi != null && dLo != null ? (dHi + dLo) / 2 : null),
      levels,
      windows: wins.sort((a, b) => (TF[a.tf].minutes - TF[b.tf].minutes)).map((w) => ({
        label: w.label, start: w.start, end: w.end, elapsed: w.end.getTime() <= now.getTime(),
        high: fp(w.hi + shiftVal), low: fp(w.lo + shiftVal),
        trend: !isNum(w.trend) ? '—' : w.trend > 0 ? 'Up' : w.trend < 0 ? 'Down' : 'Flat',
        momentum: w.momentum,
      })),
      shift,
      weak: !conviction.toLowerCase().startsWith('prime'),
    };
  }

  // ── Plan ───────────────────────────────────────────────────
  // Lots → units for this instrument.
  const unitsPerLot = Math.abs(nLots) > LOT_EPS ? nUnits / nLots
    : Math.abs(bLots) > LOT_EPS ? bUnits / bLots : Math.abs(cLots) > LOT_EPS ? cUnits / cLots : 0;
  const u = (lots: number) => fmtUnits(lots * unitsPerLot);
  const plan: NoteStep[] = [];
  const triggers: string[] = [];
  const donts: string[] = [];
  let netAfter = nLots;
  const netTxt = (lots: number) => (Math.abs(lots) < LOT_EPS ? '0' : `${lots > 0 ? '+' : '-'}${u(lots)}`);

  // Forecast windows, Taiga prices.
  const win = (tf: string) => {
    const w = forecast?.windows.find((x) => x.label === TF[tf].label && !x.elapsed);
    return w ?? null;
  };
  const limitFor = (w: NoteWindow | null, buy: boolean): number | null => {
    if (!w || pxNum <= 0) return null;
    const v = Number(buy ? w.low : w.high);
    if (!isNum(v)) return null;
    // A buy limit above the price (or a sell below) is a market order.
    if (buy ? v >= pxNum : v <= pxNum) return null;
    return v;
  };
  const venueOf = (tradeLots: number): string => {
    // Close cover where it is held on the side being reduced.
    const holders = coverLegs.filter((l) => (tradeLots > 0 ? l.brokerNetVol < 0 : l.brokerNetVol > 0))
      .sort((a, b) => Math.abs(b.brokerNetVol) - Math.abs(a.brokerNetVol));
    return holders[0] ? `DOM Trader · ${holders[0].lp.replace(/^Coverage-/, '')}` : 'DOM Trader';
  };
  const verb = (lots: number) => (lots > 0 ? 'Buy' : 'Sell');

  const accidental = status === 'WRONG-WAY' ? -cLots
    : status === 'REVERSED' || status === 'OVER' ? -nLots
    : status === 'ORPHAN' ? -cLots : 0;
  const residual = status === 'WRONG-WAY' ? bLots
    : status === 'PARTIAL' || status === 'NAKED' ? nLots : 0;

  if (status === 'WRONG-WAY' || status === 'REVERSED' || status === 'ORPHAN') {
    plan.push({ when: 'Now', action: `Pause the hedge rule on ${sym} and check its escalations`, venue: 'Hedging Strategies', size: '', price: '', netAfter: netTxt(netAfter) });
  } else if (status === 'OVER') {
    plan.push({ when: 'Now', action: `Check the hedge rule's ratio on ${sym}`, venue: 'Hedging Strategies', size: '', price: '', netAfter: netTxt(netAfter) });
  }

  let lastWindowEnd: Date | null = null;
  let tradeDir = 0;     // +1 buying, -1 selling: the plan's direction

  if (Math.abs(accidental) >= LOT_EPS) {
    tradeDir = accidental > 0 ? 1 : -1;
    const w = win('1hour') ?? win('2hour');
    const lim = limitFor(w, accidental > 0);
    const what = status === 'ORPHAN' ? 'cover with no book' : status === 'WRONG-WAY' ? 'wrong-way cover' : 'excess cover';
    if (!w || lim == null || Math.abs(accidental) < 1) {
      netAfter += accidental;
      plan.push({ when: 'Now', action: `${verb(accidental)} back the ${what}`, venue: venueOf(accidental), size: u(accidental), price: 'Market', netAfter: netTxt(netAfter) });
    } else {
      const first = Math.round(accidental * 50) / 100;   // half, 0.01 lot
      const rest = accidental - first;
      netAfter += first;
      plan.push({ when: 'Now', action: `${verb(first)} back half of the ${what}`, venue: venueOf(accidental), size: u(first), price: 'Market', netAfter: netTxt(netAfter) });
      netAfter += rest;
      plan.push({ when: `${timeOf(w.start > now ? w.start : now)}–${timeOf(w.end)}`, action: `${verb(rest)} back the rest at the ${w.label} window's predicted ${rest > 0 ? 'low' : 'high'}`, venue: venueOf(accidental), size: u(rest), price: `Limit ${fp(lim)}`, netAfter: netTxt(netAfter) });
      lastWindowEnd = w.end;
      const worth = usdOf(rest * unitsPerLot, pxNum - lim);
      if (worth != null) donts.push(`Do not wait for a better price on the whole size: the predicted pullback on the rest is worth about ${fmtUsd(worth)}; a move the other way costs more.`);
    }
  }

  if (Math.abs(residual) >= LOT_EPS) {
    const hedge = -residual;                       // trade that covers the residual
    const dirAgainst = forecast?.direction != null
      && ((residual < 0 && forecast.direction === 'UP') || (residual > 0 && forecast.direction === 'DOWN'));
    const clientsWinning = bPnl < 0;
    if (!forecast || forecast.direction == null) {
      plan.push({ when: 'Today', action: `Decide on the client book: keep it as B-Book or cover it${clientsWinning ? ' (clients are in profit)' : ''}. Check the accounts on the Traders page`, venue: 'Traders', size: `0 to ${u(hedge)}`, price: '', netAfter: `${netTxt(netAfter)} to 0` });
    } else if (dirAgainst) {
      const share = clientsWinning ? 1 : 0.5;
      const size = Math.round(hedge * share * 100) / 100;
      const w = win('2hour') ?? win('1hour');
      const lim = limitFor(w, size > 0);
      if (tradeDir === 0) tradeDir = size > 0 ? 1 : -1;
      netAfter += size;
      plan.push({
        when: w ? `${timeOf(w.start > now ? w.start : now)}–${timeOf(w.end)}` : 'Now',
        action: `Cover ${share === 1 ? 'the client book' : 'half of the client book'}: NexDay sees the day ${forecast.direction === 'UP' ? 'up' : 'down'}, against the broker's ${residual > 0 ? 'long' : 'short'}${clientsWinning ? ', and the clients are in profit' : ''}. Check the accounts on the Traders page first`,
        venue: 'DOM Trader', size: u(size), price: lim != null ? `Limit ${fp(lim)}` : 'Market', netAfter: netTxt(netAfter),
      });
      if (w) lastWindowEnd = !lastWindowEnd || w.end > lastWindowEnd ? w.end : lastWindowEnd;
    } else {
      plan.push({ when: 'Today', action: `Keep the client book (${netTxt(residual)}) as B-Book: NexDay sees the day ${forecast.direction === 'UP' ? 'up' : 'down'}, in the broker's favour. Cover it if a trigger below fires`, venue: '', size: '', price: '', netAfter: netTxt(netAfter) });
      if (tradeDir === 0) tradeDir = hedge > 0 ? 1 : -1;
    }
  }

  if (lastWindowEnd) {
    plan.push({ when: timeOf(lastWindowEnd), action: 'Time stop: trade whatever is not filled at market', venue: 'DOM Trader', size: 'Remainder', price: 'Market', netAfter: netTxt(netAfter) });
  }
  if (offsetting || (Math.abs(accidental) >= LOT_EPS && coverLegs.length > 1)) {
    const roll = rolloverAt(now);
    plan.push({ when: `Before ${timeOf(roll)}`, action: 'Close offsetting LP positions against each other (rollover at 17:00 New York): gross positions at two LPs tie up margin and pay swap on both legs', venue: 'Coverage Book', size: '', price: '', netAfter: netTxt(netAfter) });
  }
  if (status === 'WRONG-WAY' || status === 'REVERSED' || status === 'ORPHAN') {
    plan.push({ when: 'Before re-enabling', action: 'Fix the hedge rule: target ratio, one retry per rejected order, then re-enable it', venue: 'Hedging Strategies', size: '', price: '', netAfter: '' });
  }
  if (plan.length === 0) {
    plan.push({ when: 'Now', action: status === 'MATCHED' ? 'Nothing to do: the cover offsets the client book' : 'Nothing to do', venue: '', size: '', price: '', netAfter: netTxt(netAfter) });
  }

  // Triggers: the daily range, against and with the plan's direction.
  if (forecast && forecast.dailyHigh !== '—' && forecast.dailyLow !== '—' && tradeDir !== 0) {
    const against = tradeDir > 0 ? `above ${forecast.dailyHigh} (predicted daily high)` : `below ${forecast.dailyLow} (predicted daily low)`;
    const withUs = tradeDir > 0 ? `below ${forecast.dailyLow} (predicted daily low)` : `above ${forecast.dailyHigh} (predicted daily high)`;
    triggers.push(`Price ${against}: the predicted range is broken against the broker. ${tradeDir > 0 ? 'Buy' : 'Sell'} everything left in the plan at market.`);
    triggers.push(`Price ${withUs}: the forecast has failed in the broker's favour. Stop trading the plan and make a new note.`);
  }

  // Do-nots.
  if (status === 'WRONG-WAY' || status === 'REVERSED' || status === 'ORPHAN' || status === 'OVER') {
    donts.unshift('Do not keep the excess cover as a view on the market: an accidental position is closed, not traded.');
  }
  if (forecast?.levels && forecast.weak) {
    donts.push(`Do not take the NexDay trading levels as a house trade: conviction is ${forecast.conviction}, opportunity ${forecast.opportunity}.`);
  }
  if (status === 'MATCHED') donts.push('Do not add cover: the book is matched.');
  if (forecast?.stale) donts.push(`Do not time trades on this prediction: it is for ${forecast.targetDate}, not today.`);

  // ── Facts for the summary (numbers exactly as shown) ───────
  const facts: { id: string; text: string }[] = [];
  const f = (id: string, text: string) => facts.push({ id, text });
  f('net', `${sym}, all platforms: the broker is ${nSide} ${net.units} (${net.lots} lots), floating P/L ${net.pnl}.`);
  f('status', `Hedge status ${status}, hedge ratio ${ratioTxt}.`);
  f('clients', `Clients: the broker is ${bSide} ${clients.units}, P/L ${clients.pnl}.`);
  f('cover', `Cover at LPs: ${cover.side.toLowerCase()} ${cover.units}, P/L ${cover.pnl}.`);
  if (usdPerMove != null) f('sens', `Sensitivity: ${sensitivity}.`);
  where.forEach((w, i) => f(`where${i}`, w));
  if (forecast) {
    f('fc', `NexDay daily forecast for ${forecast.targetDate}: trend ${forecast.trend}, direction ${forecast.trendDirection}, conviction ${forecast.conviction}, opportunity ${forecast.opportunity}, momentum ${forecast.momentum}.`);
    f('fcrange', `NexDay predicted daily range ${forecast.dailyLow} to ${forecast.dailyHigh}.`);
    if (forecast.shift) f('basis', 'NexDay levels are moved to Taiga\'s price because the two feeds differ.');
    if (forecast.stale) f('stale', 'The NexDay prediction is not for today.');
  } else {
    f('nofc', `No NexDay prediction: ${inp.predictionNote ?? 'not available'}.`);
  }
  f('plan', plan.length > 1 || plan[0]?.size ? 'A step-by-step plan is shown under the summary.' : 'The plan says there is nothing to do.');

  // ── Summary written in code (fallback for the model's) ─────
  const headline = `${sym}: the broker is ${nSide} ${net.units}, floating ${net.pnl}; hedge status ${status}.`;
  const points = [where[0]];
  if (usdPerMove != null) points.push(`Sensitivity: ${sensitivity}.`);
  if (forecast) points.push(`NexDay: trend ${forecast.trend}, conviction ${forecast.conviction}, opportunity ${forecast.opportunity}; predicted range ${forecast.dailyLow} to ${forecast.dailyHigh}.`);
  else points.push(`No NexDay prediction: ${inp.predictionNote ?? 'not available'}.`);

  return {
    key: sym, generatedAt: now, exposureAsOf: inp.exposureAsOf, symbols,
    status, ratio: ratioTxt, price: pxNum > 0 ? fp(pxNum) : '—',
    clients, cover, net, sensitivity,
    legs: legsOut, where, forecast,
    forecastNote: p ? null : (inp.predictionNote ?? 'No NexDay prediction.'),
    plan, triggers, donts, facts,
    summary: { headline, points },
  };
}
